import { create } from "zustand";

import { getAvailableFields } from "./data-request-fields";

export type CreateDataRequestStep = "entities" | "fields" | "confirmation";

export const STEPS: CreateDataRequestStep[] = ["entities", "fields", "confirmation"];

export type EntityMode = "CLIMATE_ACTION" | "PROJECT" | "INDICATOR";

export interface EntityAssignee {
  userId: string;
  fullname: string | null;
}

export interface SelectedEntity {
  entityId: string;
  entityType: EntityMode;
  entityTitle: string;
  assigneeId: string | null;
  assigneeName: string | null;
  assigneeList: EntityAssignee[];
}

export interface ConfirmationAssignment {
  assigneeId: string;
  assigneeName: string;
}

const ALL_FIELDS = null;
type FieldSelection = string[] | typeof ALL_FIELDS;

interface CreateDataRequestState {
  currentStep: CreateDataRequestStep;
  entityMode: EntityMode;
  title: string;
  dueDate: Date | null;
  selectedEntities: SelectedEntity[];
  fieldSelection: FieldSelection;
  enableDocuments: boolean;
  freeTextQuestions: string[];
  advancedFieldToggle: boolean;
  confirmationAssigneeMap: Record<string, ConfirmationAssignment>;
  resetCount: number;
}

interface CreateDataRequestActions {
  setStep: (step: CreateDataRequestStep) => void;
  nextStep: () => void;
  prevStep: () => void;
  setEntityMode: (mode: EntityMode) => void;
  setTitle: (title: string) => void;
  setDueDate: (date: Date | null) => void;
  toggleEntity: (entity: SelectedEntity) => void;
  setEntityAssignee: (input: {
    entityId: string;
    entityType: EntityMode;
    assigneeId: string | null;
    assigneeName: string | null;
  }) => void;
  setConfirmationAssignee: (entityKey: string, assignment: ConfirmationAssignment) => void;
  clearConfirmationAssignee: (entityKey: string) => void;
  setSelectedEntities: (entities: SelectedEntity[]) => void;
  setFieldSelection: (fields: FieldSelection) => void;
  setEnableDocuments: (value: boolean) => void;
  addFreeTextQuestion: () => void;
  setFreeTextQuestion: (index: number, value: string) => void;
  removeFreeTextQuestion: (index: number) => void;
  setAdvancedFieldToggle: (value: boolean) => void;
  toggleField: (fieldKey: string) => void;
  reset: () => void;
}

const initialState: CreateDataRequestState = {
  currentStep: "entities",
  entityMode: "CLIMATE_ACTION",
  title: "",
  dueDate: null,
  selectedEntities: [],
  fieldSelection: ALL_FIELDS,
  enableDocuments: false,
  freeTextQuestions: [],
  advancedFieldToggle: false,
  confirmationAssigneeMap: {},
  resetCount: 0,
};

export function entityKey(entity: SelectedEntity): string {
  return `${entity.entityType}:${entity.entityId}`;
}

export function selectedEntityTypes(entities: SelectedEntity[]): EntityMode[] {
  return [...new Set(entities.map((e) => e.entityType))] as EntityMode[];
}

function isIndicatorFamily(entityType: EntityMode): boolean {
  return entityType === "INDICATOR";
}

function resolveFamilyExclusivity(previous: SelectedEntity[], next: SelectedEntity[]): SelectedEntity[] {
  const hasIndicator = next.some((e) => isIndicatorFamily(e.entityType));
  const hasOther = next.some((e) => !isIndicatorFamily(e.entityType));
  if (!hasIndicator || !hasOther) return next;

  const previousKeySet = new Set(previous.map(entityKey));
  const indicatorWasAdded = next.some((e) => isIndicatorFamily(e.entityType) && !previousKeySet.has(entityKey(e)));
  return next.filter((e) => isIndicatorFamily(e.entityType) === indicatorWasAdded);
}

function reconcileFieldSelection(
  get: () => CreateDataRequestState & CreateDataRequestActions,
  set: (partial: Partial<CreateDataRequestState>) => void,
  updatedEntities: SelectedEntity[],
): void {
  const { advancedFieldToggle, fieldSelection } = get();
  if (!advancedFieldToggle || !fieldSelection) return;

  const entityTypeList = selectedEntityTypes(updatedEntities);
  if (entityTypeList.length === 0) return;

  const availableKeySet = new Set(getAvailableFields(entityTypeList).map((f) => f.key));
  const reconciled = fieldSelection.filter((key) => availableKeySet.has(key));
  set({ fieldSelection: reconciled });
}

export const useCreateDataRequestStore = create<CreateDataRequestState & CreateDataRequestActions>()((set, get) => ({
  ...initialState,

  setStep: (step) => set({ currentStep: step }),

  setEntityMode: (mode) => set({ entityMode: mode }),

  nextStep: () => {
    const currentIndex = STEPS.indexOf(get().currentStep);
    if (currentIndex < STEPS.length - 1) {
      set({ currentStep: STEPS[currentIndex + 1] });
    }
  },

  prevStep: () => {
    const { currentStep } = get();
    const currentIndex = STEPS.indexOf(currentStep);
    if (currentIndex > 0) {
      const updates: Partial<CreateDataRequestState> = { currentStep: STEPS[currentIndex - 1] };
      if (currentStep === "confirmation") {
        updates.confirmationAssigneeMap = {};
      }
      set(updates);
    }
  },

  setTitle: (title) => set({ title }),
  setDueDate: (dueDate) => set({ dueDate }),

  toggleEntity: (entity) => {
    const { selectedEntities } = get();
    const exists = selectedEntities.find(
      (e) => e.entityId === entity.entityId && e.entityType === entity.entityType,
    );
    const candidate = exists
      ? selectedEntities.filter((e) => !(e.entityId === entity.entityId && e.entityType === entity.entityType))
      : [...selectedEntities, entity];
    const updatedEntities = resolveFamilyExclusivity(selectedEntities, candidate);
    set({ selectedEntities: updatedEntities, confirmationAssigneeMap: {} });
    reconcileFieldSelection(get, set, updatedEntities);
  },

  setEntityAssignee: ({ entityId, entityType, assigneeId, assigneeName }) => {
    const { selectedEntities } = get();
    set({
      selectedEntities: selectedEntities.map((e) =>
        e.entityId === entityId && e.entityType === entityType ? { ...e, assigneeId, assigneeName } : e,
      ),
    });
  },

  setConfirmationAssignee: (entityKey, assignment) => {
    const { confirmationAssigneeMap } = get();
    set({ confirmationAssigneeMap: { ...confirmationAssigneeMap, [entityKey]: assignment } });
  },

  clearConfirmationAssignee: (entityKey) => {
    const { [entityKey]: _removed, ...rest } = get().confirmationAssigneeMap;
    set({ confirmationAssigneeMap: rest });
  },

  setSelectedEntities: (entities) => {
    const resolvedEntities = resolveFamilyExclusivity(get().selectedEntities, entities);
    set({ selectedEntities: resolvedEntities, confirmationAssigneeMap: {} });
    reconcileFieldSelection(get, set, resolvedEntities);
  },

  setFieldSelection: (fields) => set({ fieldSelection: fields }),

  setEnableDocuments: (value) => set({ enableDocuments: value }),

  addFreeTextQuestion: () => set({ freeTextQuestions: [...get().freeTextQuestions, ""] }),

  setFreeTextQuestion: (index, value) =>
    set({ freeTextQuestions: get().freeTextQuestions.map((question, i) => (i === index ? value : question)) }),

  removeFreeTextQuestion: (index) =>
    set({ freeTextQuestions: get().freeTextQuestions.filter((_, i) => i !== index) }),

  setAdvancedFieldToggle: (value) => {
    if (!value) {
      set({ advancedFieldToggle: false, fieldSelection: ALL_FIELDS });
    } else {
      const { fieldSelection, selectedEntities } = get();
      const selectedEntityTypeList = selectedEntityTypes(selectedEntities);
      const defaultFieldSelection = getAvailableFields(selectedEntityTypeList).map((f) => f.key);
      set({ advancedFieldToggle: true, fieldSelection: fieldSelection ?? defaultFieldSelection });
    }
  },

  toggleField: (fieldKey) => {
    const { fieldSelection } = get();
    if (!fieldSelection) return;
    if (fieldSelection.includes(fieldKey)) {
      set({ fieldSelection: fieldSelection.filter((f) => f !== fieldKey) });
    } else {
      set({ fieldSelection: [...fieldSelection, fieldKey] });
    }
  },

  reset: () => set({ ...initialState, resetCount: get().resetCount + 1 }),
}));
